import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Package, Clock, CheckCircle, XCircle, Truck, Loader2, DollarSign, User, ChevronDown, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SellerOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(
        `${API_URL}/seller/orders${params}`,
        { withCredentials: true }
      );
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await axios.put(
        `${API_URL}/seller/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Status atualizado com sucesso!');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-purple-500" />;
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
      case 'refunded':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Aguardando Pagamento',
      paid: 'Pago',
      processing: 'Em Processamento',
      shipped: 'Enviado',
      delivered: 'Entregue',
      cancelled: 'Cancelado',
      refunded: 'Reembolsado'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: 'bg-amber-100 text-amber-700',
      paid: 'bg-emerald-100 text-emerald-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      refunded: 'bg-gray-100 text-gray-700'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700';
  };

  const getNextStatuses = (currentStatus) => {
    const transitions = {
      paid: ['processing', 'cancelled'],
      processing: ['shipped', 'cancelled'],
      shipped: ['delivered'],
      delivered: ['refunded']
    };
    return transitions[currentStatus] || [];
  };

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    paid: orders.filter(o => o.status === 'paid').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    revenue: orders
      .filter(o => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status))
      .reduce((sum, o) => sum + (o.total || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="seller-orders-title">
                Pedidos Recebidos
              </h1>
              <p className="text-gray-600">{orders.length} pedidos</p>
            </div>
          </div>

          <Button onClick={fetchOrders} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Aguardando</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Pagos</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.paid}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Processando</p>
            <p className="text-2xl font-bold text-blue-600">{stats.processing}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Enviados</p>
            <p className="text-2xl font-bold text-purple-600">{stats.shipped}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Receita</p>
            <p className="text-2xl font-bold text-green-600">
              R$ {stats.revenue.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant={statusFilter === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('')}
          >
            Todos
          </Button>
          <Button
            variant={statusFilter === 'paid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('paid')}
          >
            Pagos
          </Button>
          <Button
            variant={statusFilter === 'processing' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('processing')}
          >
            Processando
          </Button>
          <Button
            variant={statusFilter === 'shipped' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('shipped')}
          >
            Enviados
          </Button>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum pedido encontrado
            </h2>
            <p className="text-gray-600">
              {statusFilter ? 'Nenhum pedido com este status' : 'Você ainda não recebeu pedidos'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Pedido</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Cliente</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Itens</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Total</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.map((order) => (
                  <tr key={order.order_id} className="hover:bg-gray-50" data-testid={`seller-order-${order.order_id}`}>
                    <td className="px-6 py-4">
                      <p className="font-mono text-sm text-gray-900">{order.order_id.slice(0, 16)}...</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{order.customer_name}</p>
                          <p className="text-xs text-gray-500">{order.customer_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">{order.items?.length || 0} itens</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-bold text-gray-900">R$ {order.total?.toFixed(2)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        {getStatusText(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedOrder(order)}
                      >
                        Detalhes
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Pedido</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4 mt-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Status</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.status)}`}>
                  {getStatusText(selectedOrder.status)}
                </span>
              </div>

              {/* Order Info */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="order-info-section">
                <h4 className="font-semibold text-slate-900 mb-2">Informações do Pedido</h4>
                <div className="text-sm text-slate-700 space-y-1">
                  <p><span className="text-slate-500">Nº do pedido:</span> <span className="font-mono">{selectedOrder.order_id}</span></p>
                  <p><span className="text-slate-500">Criado em:</span> {new Date(selectedOrder.created_at).toLocaleString('pt-BR')}</p>
                  {selectedOrder.updated_at && (
                    <p><span className="text-slate-500">Última atualização:</span> {new Date(selectedOrder.updated_at).toLocaleString('pt-BR')}</p>
                  )}
                </div>
              </div>

              {/* Customer Data (completo) */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4" data-testid="customer-info-section">
                <h4 className="font-semibold text-slate-900 mb-2">Dados do Cliente</h4>
                <div className="text-sm text-slate-700 space-y-1">
                  <p><span className="text-slate-500">Nome:</span> {selectedOrder.buyer_details?.full_name || selectedOrder.customer_name || '—'}</p>
                  <p><span className="text-slate-500">CPF:</span> {selectedOrder.buyer_details?.cpf || selectedOrder.customer_cpf || '—'}</p>
                  <p><span className="text-slate-500">E-mail:</span> {selectedOrder.buyer_details?.email || selectedOrder.customer_email || '—'}</p>
                  <p><span className="text-slate-500">Telefone:</span> {selectedOrder.buyer_details?.phone || selectedOrder.customer_phone || '—'}</p>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="shipping-address-section">
                <h4 className="font-semibold text-slate-900 mb-2">Endereço de Entrega</h4>
                {selectedOrder.shipping_address && (selectedOrder.shipping_address.street || selectedOrder.shipping_address.zip_code) ? (
                  <div className="text-sm text-slate-700 space-y-1">
                    <p><span className="text-slate-500">CEP:</span> {selectedOrder.shipping_address.zip_code || '—'}</p>
                    <p><span className="text-slate-500">Rua:</span> {selectedOrder.shipping_address.street || '—'}, {selectedOrder.shipping_address.number || 's/n'}</p>
                    {selectedOrder.shipping_address.complement && (
                      <p><span className="text-slate-500">Complemento:</span> {selectedOrder.shipping_address.complement}</p>
                    )}
                    <p><span className="text-slate-500">Bairro:</span> {selectedOrder.shipping_address.neighborhood || '—'}</p>
                    <p><span className="text-slate-500">Cidade/UF:</span> {selectedOrder.shipping_address.city || '—'} / {selectedOrder.shipping_address.state || '—'}</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600 italic">Produto digital — Não requer entrega.</p>
                )}
              </div>

              {/* Payment Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="payment-info-section">
                <h4 className="font-semibold text-slate-900 mb-2">Pagamento</h4>
                <div className="text-sm text-slate-700 space-y-1">
                  <p><span className="text-slate-500">Método:</span> {selectedOrder.payment_method || '—'}</p>
                  <p><span className="text-slate-500">ID da transação:</span> <span className="font-mono">{selectedOrder.payment_id || '—'}</span></p>
                  <p><span className="text-slate-500">Data da compra:</span> {new Date(selectedOrder.created_at).toLocaleString('pt-BR')}</p>
                  {selectedOrder.paid_at && (
                    <p><span className="text-slate-500">Data da aprovação:</span> {new Date(selectedOrder.paid_at).toLocaleString('pt-BR')}</p>
                  )}
                  <p><span className="text-slate-500">Status:</span> <span className="font-semibold">{selectedOrder.payment_status || selectedOrder.status}</span></p>
                  <p><span className="text-slate-500">Valor pago:</span> <span className="font-semibold text-emerald-700">R$ {selectedOrder.total?.toFixed(2)}</span></p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Itens</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.name} className="w-10 h-10 object-cover rounded" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-gray-500">Qtd: {item.quantity}</p>
                        </div>
                      </div>
                      <p className="font-medium">R$ {item.total_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="font-medium">Total</span>
                <span className="text-xl font-bold text-indigo-600">
                  R$ {selectedOrder.total?.toFixed(2)}
                </span>
              </div>

              {/* Actions */}
              {getNextStatuses(selectedOrder.status).length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-gray-900 mb-2">Atualizar Status</h4>
                  <div className="flex gap-2 flex-wrap">
                    {getNextStatuses(selectedOrder.status).map((status) => (
                      <Button
                        key={status}
                        size="sm"
                        variant={status === 'cancelled' ? 'destructive' : 'default'}
                        onClick={() => updateOrderStatus(selectedOrder.order_id, status)}
                        disabled={updatingStatus === selectedOrder.order_id}
                      >
                        {updatingStatus === selectedOrder.order_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          getStatusText(status)
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
